import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type StoryTourState,
  type StoryTourStep,
  INITIAL_STORY_TOUR_STATE,
  advanceStoryTour,
  goToStoryTourStep,
  isStoryTourEligible,
  readStoryTourStorage,
  resolveStoryTourStep,
  skipStoryTour,
  startStoryTour,
  writeStoryTourStorage,
} from './storyTour';

/**
 * Wires storyTour.ts's pure step-machine to React state + localStorage +
 * ONE auto-start decision — mirrors useOnboardingGate.ts/useQuickStartGuide.ts's
 * own thin-hook shape (a pure module underneath, this file only holds the
 * useState/useEffect/useCallback plumbing).
 *
 * Auto-start reads `roleCount` from the SAME GET /corpus/roles signal
 * useOnboardingGate already fetches — this hook takes it as a PARAMETER
 * rather than issuing its own corpus fetch (App.tsx passes gate.corpus.roleCount
 * through), so mounting this hook never adds a second network round-trip for
 * a value the app already has in hand. Auto-starts exactly once per account
 * (guarded by the persisted `seen` flag) when:
 *   1. roleCount reads eligible (confirmed 0 — a genuinely first-run-shaped
 *      account, never "unknown" or "has data"), AND
 *   2. the modal Onboarding wizard has finished/been skipped (`onboardingDone`
 *      — App.tsx passes gate.step === 'done'), so the tour and that overlay
 *      never show at the same time, AND
 *   3. this account has never seen the tour trigger before (seen:false).
 *
 * Manual re-run (Settings' "Show me around" row) calls `start()` directly,
 * bypassing all three auto-start gates — a person can always ask to see it
 * again regardless of corpus state or prior dismissal.
 */
export function useStoryTour(roleCount: number | null, onboardingDone: boolean) {
  const [state, setState] = useState<StoryTourState>(() => {
    const stored = readStoryTourStorage();
    return { stepIdx: stored.stepIdx, dismissed: stored.dismissed, completed: stored.completed };
  });
  const hasAutoStarted = useRef(readStoryTourStorage().seen);

  const persist = useCallback((next: StoryTourState) => {
    const priorSeen = readStoryTourStorage().seen;
    writeStoryTourStorage({
      seen: priorSeen || next.stepIdx >= 0,
      stepIdx: next.stepIdx,
      dismissed: next.dismissed,
      completed: next.completed,
    });
    setState(next);
  }, []);

  useEffect(() => {
    if (hasAutoStarted.current) return;
    if (!onboardingDone) return;
    if (!isStoryTourEligible(roleCount)) return;
    hasAutoStarted.current = true;
    persist(startStoryTour(state));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleCount, onboardingDone, persist]);

  const start = useCallback(() => {
    hasAutoStarted.current = true;
    persist(startStoryTour(state));
  }, [state, persist]);

  const advance = useCallback(() => {
    persist(advanceStoryTour(state));
  }, [state, persist]);

  const goTo = useCallback(
    (step: StoryTourStep) => {
      persist(goToStoryTourStep(state, step));
    },
    [state, persist],
  );

  const skip = useCallback(() => {
    persist(skipStoryTour(state));
  }, [state, persist]);

  return { step: resolveStoryTourStep(state), start, advance, goTo, skip };
}
